import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { ChatUnreadProvider } from './contexts/ChatUnreadContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import ProtectedRoute from './components/ProtectedRoute';
import InstallPrompt from './components/InstallPrompt';
import { onForegroundMessage } from './utils/push';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import SetPassword from './pages/SetPassword';
import PendingApproval from './pages/PendingApproval';
import Dashboard from './pages/Dashboard';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import Songs from './pages/Songs';
import People from './pages/People';
import RoleManagement from './pages/RoleManagement';
import Settings from './pages/Settings';
import HelpDocs from './pages/HelpDocs';
import ServiceTypeManagement from './pages/ServiceTypeManagement';
import ChatPage from './pages/ChatPage';
import Lineups from './pages/Lineups';
import LineupDetail from './pages/LineupDetail';
import '@/App.css';

function App() {
  useEffect(() => {
    const unsubscribe = onForegroundMessage((payload) => {
      // data-only messages: title/body are in payload.data (not payload.notification)
      const title = payload.data?.title || payload.notification?.title || 'Church Service Planning';
      const body  = payload.data?.body  || payload.notification?.body  || '';
      const url   = payload.data?.url   || '/';

      // Show an OS-level notification banner even while the app is in the foreground.
      // This requires Notification permission (already granted for push to work).
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        navigator.serviceWorker?.ready.then(reg => {
          reg.showNotification(title, {
            body,
            icon: '/icon-192.png',
            badge: '/icon-192.png',
            vibrate: [200, 100, 200],
            data: { url },
            tag: 'csm-notification',
          });
        }).catch(() => {
          // Fallback: in-app toast only
          toast.info(title, { description: body });
        });
      } else {
        // Notification API not available — show in-app toast
        toast.info(title, { description: body });
      }
    });
    return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
  }, []);

  return (
    <BrowserRouter>
      <ThemeProvider>
      <AuthProvider>
        <ChatUnreadProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/set-password" element={<SetPassword />} />
          <Route path="/pending-approval" element={<PendingApproval />} />
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services"
            element={
              <ProtectedRoute>
                <Services />
              </ProtectedRoute>
            }
          />
          <Route
            path="/services/:serviceId"
            element={
              <ProtectedRoute>
                <ServiceDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-types"
            element={
              <ProtectedRoute>
                <ServiceTypeManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/chat/:chatId"
            element={
              <ProtectedRoute>
                <ChatPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineups"
            element={
              <ProtectedRoute>
                <Lineups />
              </ProtectedRoute>
            }
          />
          <Route
            path="/lineups/:lineupId"
            element={
              <ProtectedRoute>
                <LineupDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/songs"
            element={
              <ProtectedRoute>
                <Songs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people"
            element={
              <ProtectedRoute>
                <People />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute>
                <RoleManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/help"
            element={
              <ProtectedRoute>
                <HelpDocs />
              </ProtectedRoute>
            }
          />
          <Route path="/" element={<Navigate to="/login" replace />} />
        </Routes>
        <Toaster position="top-right" />
        <InstallPrompt />
        </ChatUnreadProvider>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
